import React, { useCallback, useMemo } from 'react';
import noop from 'lodash/noop';

import { splice } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import { Box, Input, Select, Stack, Typography } from '@lyearn/ui';
import { ListWithTempId } from '@/hooks/useList/useListWithTempId';
import LikertScale from '@/modules/user-survey/components/LikertScale';
import { DEFAULT_LIKERT_LABEL_CONFIG } from '@/modules/user-survey/components/LikertScale/utils';
import {
  CustomRatingComponentInput,
  ExerciseQuestionInput,
  RatingLabelComponentInput,
} from '@/types/schema';

type LikertConfigEditorProps = {
  index: number;
  question: ListWithTempId<ExerciseQuestionInput>;
  onChangeQuestion: (index: number, question: ListWithTempId<ExerciseQuestionInput>) => void;
};

enum ScaleType {
  Likert = 'likert',
  Custom = 'custom',
}

export function getDefaultLikertConfig(t: (key: string) => string): CustomRatingComponentInput {
  return {
    ratingLabels: DEFAULT_LIKERT_LABEL_CONFIG.map(({ labelKey, value }) => ({
      labelName: t(labelKey),
      rating: value,
    })),
  };
}

function LikertConfigEditor({ index, question, onChangeQuestion }: LikertConfigEditorProps) {
  const { t } = useTranslation('UserSurvey');

  const isCustom = !!question.customRatingComponent;

  const scaleTypeOptions = useMemo(
    () => [
      { label: t('Exercise:Creator.LikertScale'), value: ScaleType.Likert },
      { label: t('Exercise:Creator.CustomScale'), value: ScaleType.Custom },
    ],
    [t],
  );

  const onScaleTypeChange = useCallback(
    (value: ScaleType | null) => {
      if (value === ScaleType.Custom) {
        onChangeQuestion(index, {
          ...question,
          customRatingComponent: getDefaultLikertConfig(t),
        });
      } else {
        onChangeQuestion(index, {
          ...question,
          customRatingComponent: undefined,
        });
      }
    },
    [index, question, onChangeQuestion, t],
  );

  const onLabelChange = useCallback(
    (labelIndex: number, text: string) => {
      const ratingLabels = question.customRatingComponent!
        .ratingLabels as RatingLabelComponentInput[];
      const newLabels = splice(ratingLabels, labelIndex, 1, {
        ...ratingLabels[labelIndex],
        labelName: text,
      });
      onChangeQuestion(index, {
        ...question,
        customRatingComponent: { ratingLabels: newLabels },
      });
    },
    [index, question, onChangeQuestion],
  );

  return (
    <Stack direction="col" gapY={16}>
      <Stack direction="col" gapY={4}>
        <Typography color="text-text-secondary" variant="body-short-02">
          {t('Exercise:Creator.ScaleType')}
        </Typography>
        <Select
          disableClearable
          getOptionLabel={(option) => option.label}
          options={scaleTypeOptions}
          value={isCustom ? ScaleType.Custom : ScaleType.Likert}
          onChange={onScaleTypeChange}
        />
      </Stack>
      <LikertScale
        ratingLabels={
          isCustom
            ? (question.customRatingComponent!.ratingLabels as RatingLabelComponentInput[])
            : undefined
        }
        onChange={noop}
      />
      {isCustom ? (
        <Stack direction="col" gapY={12}>
          <Box className="rounded-8 bg-bg-secondary p-12">
            <Typography color="text-text-paragraph" variant="body-long-02">
              {t('Exercise:Creator.CustomScaleDescription')}
            </Typography>
          </Box>
          <Stack direction="col" gapY={8}>
            {(question.customRatingComponent!.ratingLabels as RatingLabelComponentInput[]).map(
              (label, labelIndex) => (
                <Input
                  key={label.rating}
                  placeholder={t('Exercise:Creator.LikertLevelPlaceholder', {
                    count: labelIndex + 1,
                  })}
                  value={label.labelName ?? ''}
                  onChangeText={(text) => onLabelChange(labelIndex, text)}
                />
              ),
            )}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}

export default React.memo(LikertConfigEditor);
